import { useCallback, useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { useCurrentStore } from '../../context/CurrentStoreContext'
import { Card, CardHeader } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { ConfirmModal } from '../../components/ui/ConfirmModal'
import { SkeletonListPage } from '../../components/ui/Skeleton'
import { StatusModal, useStatusModal } from '../../components/ui/StatusModal'
import { StaffModulePicker } from '../../components/team/StaffModulePicker'
import { staffModuleLabel, type StaffModuleCode } from '../../config/staffModules'
import * as staffService from '../../services/staffService'
import { fetchSubscription, type MerchantSubscription } from '../../services/billingApi'
import { getUserFacingErrorMessage, toUserFacingApiError } from '../../utils/userFacingError'

type ConfirmAction =
  | { kind: 'reset'; user: staffService.TeamMemberRecord }
  | { kind: 'deactivate'; user: staffService.TeamMemberRecord }
  | null

const DEFAULT_NEW_STAFF_MODULES: StaffModuleCode[] = ['dashboard', 'catalog']

export default function TeamPage() {
  const { user } = useAuth()
  const { currentStoreId: storeId } = useCurrentStore()
  const { openSuccess, openError, statusModalProps } = useStatusModal()
  const [members, setMembers] = useState<staffService.TeamMemberRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const [addSubmitting, setAddSubmitting] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [addModules, setAddModules] = useState<StaffModuleCode[]>(DEFAULT_NEW_STAFF_MODULES)
  const [editMember, setEditMember] = useState<staffService.TeamMemberRecord | null>(null)
  const [editModules, setEditModules] = useState<StaffModuleCode[]>([])
  const [editSubmitting, setEditSubmitting] = useState(false)
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null)
  const [subscription, setSubscription] = useState<MerchantSubscription | null>(null)

  const load = useCallback(async () => {
    if (storeId == null) {
      setMembers([])
      setSubscription(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const [{ items }, sub] = await Promise.all([
        staffService.listTeamMembers(storeId),
        fetchSubscription().catch(() => null),
      ])
      setMembers(Array.isArray(items) ? items : [])
      setSubscription(sub)
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Failed to load team'))
    } finally {
      setLoading(false)
    }
  }, [storeId])

  useEffect(() => {
    void load()
  }, [load])

  if (user?.role !== 'org_admin') {
    return <Navigate to="/" replace />
  }

  function openEditModules(member: staffService.TeamMemberRecord) {
    setEditMember(member)
    setEditModules((member.modules ?? []) as StaffModuleCode[])
  }

  async function handleAddStaff(e: React.FormEvent) {
    e.preventDefault()
    if (addModules.length === 0) {
      openError({ title: 'Select modules', description: 'Choose at least one module for this staff member.' })
      return
    }
    setAddSubmitting(true)
    try {
      const result = await staffService.createStaffMember(storeId!, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        modules: addModules,
      })
      await load()
      setAddOpen(false)
      setFirstName('')
      setLastName('')
      setEmail('')
      setAddModules(DEFAULT_NEW_STAFF_MODULES)
      openSuccess({
        title: 'Staff member added',
        description: result.emailSent
          ? 'Invite email sent with login details.'
          : 'Staff member added, but the invite email could not be sent.',
      })
    } catch (e) {
      const apiErr = toUserFacingApiError(e, 'Could not add staff member')
      openError({
        title: 'Could not add staff',
        description:
          apiErr.code === 'STAFF_LIMIT_REACHED'
            ? 'Your subscription plan has no remaining staff seats. Upgrade your plan or remove a staff member to add another.'
            : apiErr.message,
      })
    } finally {
      setAddSubmitting(false)
    }
  }

  async function handleSaveModules() {
    if (!editMember) return
    if (editModules.length === 0) {
      openError({ title: 'Select modules', description: 'Choose at least one module.' })
      return
    }
    setEditSubmitting(true)
    try {
      await staffService.updateStaffModules(storeId!, editMember.id, editModules)
      await load()
      setEditMember(null)
      openSuccess({
        title: 'Access updated',
        description: `Module access saved for ${editMember.email}. They may need to sign in again to see changes.`,
      })
    } catch (e) {
      openError({
        title: 'Could not save access',
        description: getUserFacingErrorMessage(e, 'Failed to update module access'),
      })
    } finally {
      setEditSubmitting(false)
    }
  }

  async function executeConfirmAction() {
    if (!confirmAction) return
    const target = confirmAction.user
    try {
      if (confirmAction.kind === 'reset') {
        const result = await staffService.resetStaffCredentials(storeId!, target.id)
        await load()
        openSuccess({
          title: 'Login reset',
          description: result.emailSent
            ? `Login details emailed to ${target.email}.`
            : `Password updated for ${target.email}, but the email could not be sent.`,
        })
      } else {
        await staffService.deactivateStaffMember(storeId!, target.id)
        await load()
        openSuccess({
          title: 'Staff deactivated',
          description: `${target.email} can no longer sign in.`,
        })
      }
    } catch (e) {
      openError({
        title: 'Action failed',
        description: getUserFacingErrorMessage(e, 'Action failed'),
      })
    } finally {
      setConfirmAction(null)
    }
  }

  function roleLabel(role: string) {
    if (role === 'org_admin' || role === 'store_owner') return 'Shop owner'
    if (role === 'staff') return 'Staff'
    return role
  }

  function formatModules(modules: string[] | undefined) {
    if (!modules?.length) return '—'
    return modules.map(staffModuleLabel).join(', ')
  }

  const staffSeatLabel = (() => {
    if (!subscription) return null
    if (subscription.maxStaffCount == null) {
      return `${subscription.staffCount} staff · unlimited seats on your plan`
    }
    const remaining = subscription.staffSeatsRemaining ?? 0
    return `${subscription.staffCount} of ${subscription.maxStaffCount} staff seats used · ${remaining} remaining`
  })()

  const staffSeatsFull =
    subscription?.maxStaffCount != null && (subscription.staffSeatsRemaining ?? 0) <= 0

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Team"
          description={
            staffSeatLabel
              ? `${staffSeatLabel}. Add staff who can sign in to store admin and assign module access.`
              : 'Add staff who can sign in to store admin. Assign module access so each person only sees what they need.'
          }
          action={
            <Button
              type="button"
              onClick={() => setAddOpen(true)}
              disabled={staffSeatsFull || storeId == null}
              title={staffSeatsFull ? 'Your plan has no remaining staff seats' : undefined}
            >
              Add staff
            </Button>
          }
        />

        {loading ? (
          <SkeletonListPage toolbarFields={0} columns={6} rows={4} />
        ) : error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : members.length === 0 ? (
          <p className="text-sm text-muted-foreground">No team members found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[48rem] text-left text-sm">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="pb-2 pr-4 font-medium">Name</th>
                  <th className="pb-2 pr-4 font-medium">Email</th>
                  <th className="pb-2 pr-4 font-medium">Role</th>
                  <th className="pb-2 pr-4 font-medium">Modules</th>
                  <th className="pb-2 pr-4 font-medium">Login</th>
                  <th className="pb-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => {
                  const isStaff = member.role === 'staff'
                  return (
                    <tr key={member.id} className="border-b border-border/60 last:border-0">
                      <td className="py-3 pr-4 font-medium text-foreground">
                        {[member.firstName, member.lastName].filter(Boolean).join(' ') || '—'}
                      </td>
                      <td className="py-3 pr-4 text-foreground">{member.email}</td>
                      <td className="py-3 pr-4 text-foreground">{roleLabel(member.role)}</td>
                      <td className="py-3 pr-4 text-foreground">{isStaff ? formatModules(member.modules) : 'All modules'}</td>
                      <td className="py-3 pr-4 text-foreground">
                        {member.mustChangePassword ? 'Pending first login' : 'Activated'}
                      </td>
                      <td className="py-3 text-right">
                        {isStaff ? (
                          <div className="flex flex-wrap justify-end gap-2">
                            <Button
                              type="button"
                              variant="secondary"
                              className="h-8 text-xs"
                              onClick={() => openEditModules(member)}
                            >
                              Edit access
                            </Button>
                            <Button
                              type="button"
                              variant="secondary"
                              className="h-8 text-xs"
                              onClick={() => setConfirmAction({ kind: 'reset', user: member })}
                            >
                              Reset login
                            </Button>
                            <Button
                              type="button"
                              variant="danger"
                              className="h-8 text-xs"
                              onClick={() => setConfirmAction({ kind: 'deactivate', user: member })}
                            >
                              Deactivate
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        open={addOpen}
        onClose={() => {
          if (!addSubmitting) setAddOpen(false)
        }}
        title="Add staff member"
        size="md"
        footer={
          <>
            <Button type="button" variant="secondary" disabled={addSubmitting} onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" form="add-staff-form" disabled={addSubmitting}>
              {addSubmitting ? 'Adding…' : 'Add staff'}
            </Button>
          </>
        }
      >
        <form id="add-staff-form" className="space-y-4" onSubmit={(e) => void handleAddStaff(e)}>
          <Input
            id="staff-first-name"
            label="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            autoComplete="given-name"
          />
          <Input
            id="staff-last-name"
            label="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            autoComplete="family-name"
          />
          <Input
            id="staff-email"
            type="email"
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
          <StaffModulePicker value={addModules} onChange={setAddModules} disabled={addSubmitting} />
        </form>
      </Modal>

      <Modal
        open={Boolean(editMember)}
        onClose={() => {
          if (!editSubmitting) setEditMember(null)
        }}
        title="Edit module access"
        size="md"
        footer={
          <>
            <Button type="button" variant="secondary" disabled={editSubmitting} onClick={() => setEditMember(null)}>
              Cancel
            </Button>
            <Button type="button" disabled={editSubmitting} onClick={() => void handleSaveModules()}>
              {editSubmitting ? 'Saving…' : 'Save access'}
            </Button>
          </>
        }
      >
        {editMember ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Update which sections <span className="font-medium text-foreground">{editMember.email}</span> can use.
            </p>
            <StaffModulePicker value={editModules} onChange={setEditModules} disabled={editSubmitting} />
          </div>
        ) : null}
      </Modal>

      <ConfirmModal
        open={Boolean(confirmAction)}
        onClose={() => setConfirmAction(null)}
        title={confirmAction?.kind === 'deactivate' ? 'Deactivate staff member?' : 'Reset staff login?'}
        description={
          confirmAction?.kind === 'deactivate'
            ? `${confirmAction.user.email} will no longer be able to sign in.`
            : `Send new temporary login details to ${confirmAction?.user.email}?`
        }
        confirmLabel={confirmAction?.kind === 'deactivate' ? 'Deactivate' : 'Reset login'}
        variant={confirmAction?.kind === 'deactivate' ? 'danger' : 'primary'}
        onConfirm={executeConfirmAction}
      />

      <StatusModal {...statusModalProps} />
    </div>
  )
}
